import { createModel } from 'xstate/lib/model';

import type { AddressType } from '@sg/garnish';
import { emptyEvent, passthroughEvent } from '@sg/garnish';
import type { DeliveryOrderDetailInput } from '@sg/graphql-schema';

import {
  type ReorderLineItemMutation,
  type ReorderOrderMutation,
} from '../GraphQL/Reorder.generated';

export type ReorderStep =
  | 'idle'
  | 'activeBagWarning'
  | 'confirmingGroupOrderReorder'
  | 'locationSearch'
  | 'locationConfirmation'
  | 'conflictReview'
  | undefined;

export type ReorderEvent = Readonly<{
  // reorder mutation response with conflicts / success data.
  response?: ReorderOrderMutation | ReorderLineItemMutation;

  // order information.
  orderId?: string;
  lineItemId?: string;
  orderType?: AddressType;

  // restaurant / delivery information.
  deliveryAddressName?: string;
  deliveryOrderDetail?: Partial<DeliveryOrderDetailInput>;
  restaurantId?: string;
  restaurantSlug?: string;
  restaurantName?: string;

  // for loading indicators
  reordering?: boolean;

  // variables used only to meet telemetry requirements.
  reorderResult?: string; // reorder.__typename
  orderTotal?: number; // total cost of the order
  conflictType?: string; // reorder.conflict.type
  userInitiated?: boolean; // as opposed to automatic reorder

  // conditional step for which to execute this transition.
  step?: ReorderStep;
}>;

export type ReorderEventKey<Keys extends keyof ReorderEvent = keyof ReorderEvent> = keyof Pick<
  ReorderEvent,
  Keys
>;

export const validReorderEventKeys: readonly ReorderEventKey[] = [
  'response',
  'orderId',
  'lineItemId',
  'orderType',
  'deliveryAddressName',
  'deliveryOrderDetail',
  'restaurantId',
  'restaurantSlug',
  'restaurantName',
  'reordering',
  'reorderResult',
  'orderTotal',
  'conflictType',
  'userInitiated',
];

const initialContext = {
  reordering: false,
  response: undefined as ReorderOrderMutation | ReorderLineItemMutation | undefined,
  orderId: '',
  lineItemId: '',
  orderType: 'pickup' as AddressType,
  deliveryAddressName: '',
  deliveryOrderDetail: undefined as Partial<DeliveryOrderDetailInput> | undefined,
  restaurantId: '',
  restaurantSlug: '',
  restaurantName: '',
  previousStep: undefined as ReorderStep,
  groupOrderReorderRequest: undefined as GroupOrderReorderRequest | undefined,
};

export const reorderModel = createModel(initialContext, {
  events: {
    ACTIVE_BAG_WARNING: passthroughEvent as (event: ReorderEvent) => ReorderEvent,
    CHANGE_LOCATION: passthroughEvent as (event: ReorderEvent) => ReorderEvent,
    CONFIRM_LOCATION: passthroughEvent as (event: ReorderEvent) => ReorderEvent,
    CONFLICT_REVIEW: passthroughEvent as (event: ReorderEvent) => ReorderEvent,
    SET_REORDERING: passthroughEvent as (event: ReorderEvent) => ReorderEvent,
    REQUEST_GROUP_ORDER_REORDER_CONFIRMATION: passthroughEvent as (
      event: GroupOrderReorderConfirmationEvent,
    ) => GroupOrderReorderConfirmationEvent,
    CANCEL_GROUP_ORDER_REORDER_CONFIRMATION: emptyEvent,
    CONFIRM_GROUP_ORDER_REORDER: emptyEvent,
    FINISH_REORDER: emptyEvent,
    CANCEL_REORDER: passthroughEvent as (event: ReorderEvent) => ReorderEvent,
  },
});

export const reorderMachine = reorderModel.createMachine({
  predictableActionArguments: true, // https://xstate.js.org/docs/guides/actions.html
  id: 'sgReorderMachine',
  context: reorderModel.initialContext,
  initial: 'idle',
  states: {
    idle: {
      on: {
        REQUEST_GROUP_ORDER_REORDER_CONFIRMATION: {
          target: 'confirmingGroupOrderReorder',
          actions: reorderModel.assign({
            groupOrderReorderRequest: (_context, event) => ({
              event: event.reorderEvent,
              originStep: event.originStep,
            }),
          }),
        },
        ACTIVE_BAG_WARNING: {
          target: 'activeBagWarning',
          actions: reorderModel.assign({
            orderId(context, event) {
              const { orderId, lineItemId } = event;

              return lineItemId ? '' : (orderId ?? context.orderId);
            },
            lineItemId(context, event) {
              const { orderId, lineItemId } = event;

              return orderId ? '' : (lineItemId ?? context.lineItemId);
            },
            orderType(context, event) {
              const { orderType } = event;

              return orderType ?? context.orderType;
            },
            deliveryAddressName(context, event) {
              const { deliveryAddressName } = event;

              return deliveryAddressName ?? context.deliveryAddressName;
            },
            deliveryOrderDetail(context, event) {
              const { deliveryOrderDetail } = event;

              return deliveryOrderDetail ?? context.deliveryOrderDetail;
            },
            restaurantId(context, event) {
              const { restaurantId } = event;

              return restaurantId ?? context.restaurantId;
            },
            restaurantSlug(context, event) {
              const { restaurantSlug } = event;

              return restaurantSlug ?? context.restaurantSlug;
            },
            restaurantName(context, event) {
              const { restaurantName } = event;

              return restaurantName ?? context.restaurantName;
            },
            previousStep: undefined,
          }),
        },
        CHANGE_LOCATION: {
          target: 'locationSearch',
          actions: reorderModel.assign({
            orderId(context, event) {
              const { orderId, lineItemId } = event;

              return lineItemId ? '' : (orderId ?? context.orderId);
            },
            lineItemId(context, event) {
              const { orderId, lineItemId } = event;

              return orderId ? '' : (lineItemId ?? context.lineItemId);
            },
            orderType(context, event) {
              const { orderType } = event;

              return orderType ?? context.orderType;
            },
            deliveryAddressName(context, event) {
              const { deliveryAddressName } = event;

              return deliveryAddressName ?? context.deliveryAddressName;
            },
            deliveryOrderDetail(context, event) {
              const { deliveryOrderDetail } = event;

              return deliveryOrderDetail ?? context.deliveryOrderDetail;
            },
            restaurantId(context, event) {
              const { restaurantId } = event;

              return restaurantId ?? context.restaurantId;
            },
            restaurantSlug(context, event) {
              const { restaurantSlug } = event;

              return restaurantSlug ?? context.restaurantSlug;
            },
            restaurantName(context, event) {
              const { restaurantName } = event;

              return restaurantName ?? context.restaurantName;
            },
            previousStep: undefined,
          }),
        },
        CONFIRM_LOCATION: {
          target: 'locationConfirmation',
          actions: reorderModel.assign({
            orderId(context, event) {
              const { orderId, lineItemId } = event;

              return lineItemId ? '' : (orderId ?? context.orderId);
            },
            lineItemId(context, event) {
              const { orderId, lineItemId } = event;

              return orderId ? '' : (lineItemId ?? context.lineItemId);
            },
            orderType(context, event) {
              const { orderType } = event;

              return orderType ?? context.orderType;
            },
            deliveryAddressName(context, event) {
              const { deliveryAddressName } = event;

              return deliveryAddressName ?? context.deliveryAddressName;
            },
            deliveryOrderDetail(context, event) {
              const { deliveryOrderDetail } = event;

              return deliveryOrderDetail ?? context.deliveryOrderDetail;
            },
            restaurantId(context, event) {
              const { restaurantId } = event;

              return restaurantId ?? context.restaurantId;
            },
            restaurantSlug(context, event) {
              const { restaurantSlug } = event;

              return restaurantSlug ?? context.restaurantSlug;
            },
            restaurantName(context, event) {
              const { restaurantName } = event;

              return restaurantName ?? context.restaurantName;
            },
            previousStep: undefined,
          }),
        },
        CONFLICT_REVIEW: {
          target: 'conflictReview',
          actions: reorderModel.assign({
            orderId(context, event) {
              const { orderId, lineItemId } = event;

              return lineItemId ? '' : (orderId ?? context.orderId);
            },
            lineItemId(context, event) {
              const { orderId, lineItemId } = event;

              return orderId ? '' : (lineItemId ?? context.lineItemId);
            },
            orderType(context, event) {
              const { orderType } = event;

              return orderType ?? context.orderType;
            },
            deliveryAddressName(context, event) {
              const { deliveryAddressName } = event;

              return deliveryAddressName ?? context.deliveryAddressName;
            },
            deliveryOrderDetail(context, event) {
              const { deliveryOrderDetail } = event;

              return deliveryOrderDetail ?? context.deliveryOrderDetail;
            },
            restaurantId(context, event) {
              const { restaurantId } = event;

              return restaurantId ?? context.restaurantId;
            },
            restaurantSlug(context, event) {
              const { restaurantSlug } = event;

              return restaurantSlug ?? context.restaurantSlug;
            },
            restaurantName(context, event) {
              const { restaurantName } = event;

              return restaurantName ?? context.restaurantName;
            },
            response(context, event) {
              const { response } = event;

              return response ?? context.response;
            },
            reordering: false,
            previousStep: undefined,
          }),
        },
        SET_REORDERING: {
          target: 'locationSearch',
          actions: reorderModel.assign({
            orderId(context, event) {
              const { orderId, lineItemId } = event;

              return lineItemId ? '' : (orderId ?? context.orderId);
            },
            lineItemId(context, event) {
              const { orderId, lineItemId } = event;

              return orderId ? '' : (lineItemId ?? context.lineItemId);
            },
            orderType(context, event) {
              const { orderType } = event;

              return orderType ?? context.orderType;
            },
            deliveryAddressName(context, event) {
              const { deliveryAddressName } = event;

              return deliveryAddressName ?? context.deliveryAddressName;
            },
            deliveryOrderDetail(context, event) {
              const { deliveryOrderDetail } = event;

              return deliveryOrderDetail ?? context.deliveryOrderDetail;
            },
            restaurantId(context, event) {
              const { restaurantId } = event;

              return restaurantId ?? context.restaurantId;
            },
            restaurantSlug(context, event) {
              const { restaurantSlug } = event;

              return restaurantSlug ?? context.restaurantSlug;
            },
            restaurantName(context, event) {
              const { restaurantName } = event;

              return restaurantName ?? context.restaurantName;
            },
            response(context, event) {
              const { response } = event;

              return response ?? context.response;
            },
            previousStep: undefined,
            reordering: true,
          }),
        },
      },
    },
    locationSearch: {
      on: {
        REQUEST_GROUP_ORDER_REORDER_CONFIRMATION: {
          target: 'confirmingGroupOrderReorder',
          actions: reorderModel.assign({
            groupOrderReorderRequest: (_context, event) => ({
              event: event.reorderEvent,
              originStep: event.originStep,
            }),
          }),
        },
        CONFIRM_LOCATION: {
          target: 'locationConfirmation',
          actions: reorderModel.assign({
            previousStep: undefined,
          }),
        },
        CONFLICT_REVIEW: {
          target: 'conflictReview',
          actions: reorderModel.assign({
            orderId(context, event) {
              const { orderId, lineItemId } = event;

              return lineItemId ? '' : (orderId ?? context.orderId);
            },
            lineItemId(context, event) {
              const { orderId, lineItemId } = event;

              return orderId ? '' : (lineItemId ?? context.lineItemId);
            },
            orderType(context, event) {
              const { orderType } = event;

              return orderType ?? context.orderType;
            },
            deliveryAddressName(_context, event) {
              const { deliveryAddressName } = event;

              return deliveryAddressName ?? '';
            },
            deliveryOrderDetail(_context, event) {
              const { deliveryOrderDetail } = event;

              return deliveryOrderDetail;
            },
            restaurantId(context, event) {
              const { restaurantId } = event;

              return restaurantId ?? context.restaurantId;
            },
            restaurantSlug(context, event) {
              const { restaurantSlug } = event;

              return restaurantSlug ?? context.restaurantSlug;
            },
            restaurantName(context, event) {
              const { restaurantName } = event;

              return restaurantName ?? context.restaurantName;
            },
            response(context, event) {
              const { response } = event;

              return response ?? context.response;
            },
            reordering: false,
            previousStep: 'locationSearch',
          }),
        },
        SET_REORDERING: {
          target: 'locationSearch',
          actions: reorderModel.assign({
            orderId(context, event) {
              const { orderId, lineItemId } = event;

              return lineItemId ? '' : (orderId ?? context.orderId);
            },
            lineItemId(context, event) {
              const { orderId, lineItemId } = event;

              return orderId ? '' : (lineItemId ?? context.lineItemId);
            },
            orderType(context, event) {
              const { orderType } = event;

              return orderType ?? context.orderType;
            },
            deliveryAddressName(context, event) {
              const { deliveryAddressName } = event;

              return deliveryAddressName ?? context.deliveryAddressName;
            },
            deliveryOrderDetail(context, event) {
              const { deliveryOrderDetail } = event;

              return deliveryOrderDetail ?? context.deliveryOrderDetail;
            },
            restaurantId(context, event) {
              const { restaurantId } = event;

              return restaurantId ?? context.restaurantId;
            },
            restaurantSlug(context, event) {
              const { restaurantSlug } = event;

              return restaurantSlug ?? context.restaurantSlug;
            },
            restaurantName(context, event) {
              const { restaurantName } = event;

              return restaurantName ?? context.restaurantName;
            },
            response(context, event) {
              const { response } = event;

              return response ?? context.response;
            },
            reordering: true,
          }),
        },
        CANCEL_REORDER: {
          target: 'idle',
          actions: reorderModel.assign(initialContext),
          cond: (_context, event: ReorderEvent) => !event.step || event.step === 'locationSearch',
        },
      },
    },
    confirmingGroupOrderReorder: {
      exit: reorderModel.assign({
        groupOrderReorderRequest: undefined,
      }),
      after: {
        30_000: {
          target: 'idle',
          actions: reorderModel.assign(initialContext),
        },
      },
      on: {
        CANCEL_GROUP_ORDER_REORDER_CONFIRMATION: {
          target: 'idle',
          actions: reorderModel.assign(initialContext),
        },
        CONFIRM_GROUP_ORDER_REORDER: {
          target: 'idle',
        },
      },
    },
    locationConfirmation: {
      on: {
        CHANGE_LOCATION: {
          target: 'locationSearch',
          actions: reorderModel.assign({
            previousStep: 'locationConfirmation',
          }),
        },
        CONFLICT_REVIEW: {
          target: 'conflictReview',
          actions: reorderModel.assign({
            response(context, event) {
              const { response } = event;

              return response ?? context.response;
            },
            reordering: false,
            previousStep: 'locationConfirmation',
          }),
        },
        SET_REORDERING: {
          target: 'locationConfirmation',
          actions: reorderModel.assign({
            orderId(context, event) {
              const { orderId, lineItemId } = event;

              return lineItemId ? '' : (orderId ?? context.orderId);
            },
            lineItemId(context, event) {
              const { orderId, lineItemId } = event;

              return orderId ? '' : (lineItemId ?? context.lineItemId);
            },
            orderType(context, event) {
              const { orderType } = event;

              return orderType ?? context.orderType;
            },
            deliveryAddressName(context, event) {
              const { deliveryAddressName } = event;

              return deliveryAddressName ?? context.deliveryAddressName;
            },
            deliveryOrderDetail(context, event) {
              const { deliveryOrderDetail } = event;

              return deliveryOrderDetail ?? context.deliveryOrderDetail;
            },
            restaurantId(context, event) {
              const { restaurantId } = event;

              return restaurantId ?? context.restaurantId;
            },
            restaurantSlug(context, event) {
              const { restaurantSlug } = event;

              return restaurantSlug ?? context.restaurantSlug;
            },
            restaurantName(context, event) {
              const { restaurantName } = event;

              return restaurantName ?? context.restaurantName;
            },
            response(context, event) {
              const { response } = event;

              return response ?? context.response;
            },
            reordering: true,
          }),
        },
        CANCEL_REORDER: {
          target: 'idle',
          actions: reorderModel.assign(initialContext),
          cond: (_context, event: ReorderEvent) =>
            !event.step || event.step === 'locationConfirmation',
        },
      },
    },
    conflictReview: {
      on: {
        CHANGE_LOCATION: {
          target: 'locationSearch',
          actions: reorderModel.assign({
            previousStep: 'conflictReview',
          }),
        },
        FINISH_REORDER: {
          target: 'idle',
          actions: reorderModel.assign(initialContext),
        },
        CANCEL_REORDER: {
          target: 'idle',
          actions: reorderModel.assign(initialContext),
          cond: (_context, event: ReorderEvent) => !event.step || event.step === 'conflictReview',
        },
      },
    },
    activeBagWarning: {
      on: {
        CONFIRM_LOCATION: {
          target: 'locationConfirmation',
          actions: reorderModel.assign({
            previousStep: 'activeBagWarning',
          }),
        },
        CANCEL_REORDER: {
          target: 'idle',
          actions: reorderModel.assign(initialContext),
          cond: (_context, event: ReorderEvent) => !event.step || event.step === 'activeBagWarning',
        },
      },
    },
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type GroupOrderReorderConfirmationEvent = {
  reorderEvent: ReorderEvent;
  originStep: ReorderStep;
};

type GroupOrderReorderRequest = {
  event: ReorderEvent;
  originStep: ReorderStep;
};
